/**
 * FoundingCountdown — live countdown for the Founding Member campaign.
 * Renders on the Pricing page inside the existing founding-hero-card.
 *
 * Two urgency signals stacked:
 *   1. Time remaining → "5d 18h 22m 11s" — ticks every second, anchored
 *      to the server's ends_at timestamp (not the client clock, so
 *      timezone tricks don't lie).
 *   2. Seats remaining → "47 of 100 seats left" with a progress bar so
 *      the closer-to-full it gets, the more visible the bar.
 *
 * Auto-hides when the campaign ends_at has passed OR remaining hits 0.
 */
import React, { useEffect, useState } from 'react';
import { Clock, Users } from 'lucide-react';

const computeRemaining = (endsAt) => {
  if (!endsAt) return null;
  const end = new Date(endsAt).getTime();
  const now = Date.now();
  const ms = end - now;
  if (ms <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  const seconds = Math.floor((ms % 60_000) / 1000);
  return { days, hours, minutes, seconds, expired: false };
};

const pad = (n) => String(n).padStart(2, '0');

const FoundingCountdown = ({ stats }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!stats?.ends_at) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [stats?.ends_at, now]);

  if (!stats?.ends_at) return null;
  const remaining = computeRemaining(stats.ends_at);
  if (!remaining || remaining.expired) return null;

  const seatsRemaining = Math.max(0, stats.remaining ?? 0);
  const maxSeats = stats.max_seats ?? 100;
  const seatsTakenPct = Math.min(
    100,
    Math.round(((maxSeats - seatsRemaining) / maxSeats) * 100),
  );

  // Critical urgency when < 24h OR < 10 seats left → red accent.
  const isCritical = remaining.days === 0 || seatsRemaining < 10;

  return (
    <div
      className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3"
      data-testid="founding-countdown"
    >
      {/* Time remaining */}
      <div
        className={`p-3 border ${isCritical ? 'border-[#fbbf24]/60 bg-[#7f1d1d]/30' : 'border-white/20 bg-white/5'}`}
        data-testid="founding-countdown-time"
      >
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/70 font-bold mb-1.5">
          <Clock size={11} />
          {isCritical ? 'Ending soon' : 'Campaign ends in'}
        </div>
        <div className="flex items-baseline gap-3 font-mono tabular-nums text-white">
          {remaining.days > 0 && (
            <div className="flex flex-col items-center" data-testid="countdown-days">
              <span className="text-2xl sm:text-3xl font-black leading-none">{remaining.days}</span>
              <span className="text-[9px] uppercase tracking-wider text-white/60 mt-0.5">days</span>
            </div>
          )}
          <div className="flex flex-col items-center" data-testid="countdown-hours">
            <span className="text-2xl sm:text-3xl font-black leading-none">{pad(remaining.hours)}</span>
            <span className="text-[9px] uppercase tracking-wider text-white/60 mt-0.5">hrs</span>
          </div>
          <div className="flex flex-col items-center" data-testid="countdown-minutes">
            <span className="text-2xl sm:text-3xl font-black leading-none">{pad(remaining.minutes)}</span>
            <span className="text-[9px] uppercase tracking-wider text-white/60 mt-0.5">min</span>
          </div>
          <div className="flex flex-col items-center" data-testid="countdown-seconds">
            <span className={`text-2xl sm:text-3xl font-black leading-none ${isCritical ? 'text-[#fbbf24]' : 'text-[#10b981]'}`}>
              {pad(remaining.seconds)}
            </span>
            <span className="text-[9px] uppercase tracking-wider text-white/60 mt-0.5">sec</span>
          </div>
        </div>
      </div>

      {/* Seats remaining */}
      <div
        className={`p-3 border ${isCritical ? 'border-[#fbbf24]/60 bg-[#7f1d1d]/30' : 'border-white/20 bg-white/5'}`}
        data-testid="founding-countdown-seats"
      >
        <div className="flex items-center justify-between gap-1.5 text-[10px] uppercase tracking-wider text-white/70 font-bold mb-1.5">
          <span className="flex items-center gap-1.5">
            <Users size={11} />
            Founding seats left
          </span>
          <span className="text-white/60 font-mono">{maxSeats - seatsRemaining}/{maxSeats} taken</span>
        </div>
        <div className="flex items-baseline gap-2 mb-2">
          <span
            className={`text-3xl sm:text-4xl font-black font-mono tabular-nums ${isCritical ? 'text-[#fbbf24]' : 'text-white'}`}
            data-testid="seats-remaining-value"
          >
            {seatsRemaining}
          </span>
          <span className="text-xs text-white/60">of {maxSeats}</span>
        </div>
        <div className="h-1.5 bg-white/10 overflow-hidden">
          <div
            className={`h-full transition-all duration-700 ${isCritical ? 'bg-[#fbbf24]' : 'bg-[#10b981]'}`}
            style={{ width: `${seatsTakenPct}%` }}
            data-testid="seats-progress-bar"
          />
        </div>
      </div>
    </div>
  );
};

export default FoundingCountdown;
